import { useRef, useState } from 'react';
import { useTeam } from '../context/TeamContext';
import { useMe } from '../hooks/useMe';
import { useUserTeams } from '../hooks/useUserTeams';
import { AssigneeAvatar } from './AssigneeAvatar';
import {
  VIEWER_ALL,
  VIEWER_SELF,
  VIEWER_TEAM_PREFIX,
  isTeamViewer,
  type ViewerValue,
} from '../utils/viewerFilter';

interface Props {
  value: ViewerValue;
  onChange: (value: ViewerValue) => void;
}

export function ViewerToggle({ value, onChange }: Props) {
  const { members } = useTeam();
  const { me } = useMe();
  const { userTeams } = useUserTeams();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Hide the widget entirely when there's nothing meaningful to filter by —
  // only one teammate visible and no user-teams configured.
  if (members.length <= 1 && userTeams.length === 0) return null;

  const selfMember = me ? members.find((m) => m.userId === me.sub) ?? null : null;
  const teamId = isTeamViewer(value);
  const selected =
    value === VIEWER_ALL || value === VIEWER_SELF || teamId
      ? null
      : members.find((m) => m.userId === value) ?? null;

  const label = (() => {
    if (value === VIEWER_ALL) return 'Everyone';
    if (value === VIEWER_SELF) return selfMember ? selfMember.displayName : 'You';
    if (teamId) {
      const t = userTeams.find((x) => x.teamId === teamId);
      return t ? t.displayName : 'Team';
    }
    return selected?.displayName ?? 'Unknown';
  })();

  return (
    <div
      className="viewer-toggle"
      ref={wrapRef}
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        className={`viewer-toggle-btn ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="viewer-toggle-label muted">Viewing:</span>
        {value === VIEWER_ALL || teamId ? (
          <span className="viewer-everyone-dot" aria-hidden="true">
            {teamId ? '◉' : '∞'}
          </span>
        ) : selected ? (
          <AssigneeAvatar member={selected} size={18} />
        ) : selfMember ? (
          <AssigneeAvatar member={selfMember} size={18} />
        ) : (
          <span className="viewer-everyone-dot" aria-hidden="true">∞</span>
        )}
        <span className="viewer-toggle-name">{label}</span>
        <span className="viewer-toggle-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul className="viewer-toggle-menu" role="listbox">
          <li>
            <button
              type="button"
              className={value === VIEWER_ALL ? 'is-selected' : ''}
              onClick={() => {
                onChange(VIEWER_ALL);
                setOpen(false);
              }}
              role="option"
              aria-selected={value === VIEWER_ALL}
            >
              <span className="viewer-everyone-dot" aria-hidden="true">∞</span>
              <span>Everyone</span>
            </button>
          </li>
          {selfMember && (
            <li>
              <button
                type="button"
                className={value === VIEWER_SELF ? 'is-selected' : ''}
                onClick={() => {
                  onChange(VIEWER_SELF);
                  setOpen(false);
                }}
                role="option"
                aria-selected={value === VIEWER_SELF}
              >
                <AssigneeAvatar member={selfMember} size={18} />
                <span>Just me</span>
              </button>
            </li>
          )}
          {members.filter((m) => !selfMember || m.userId !== selfMember.userId).length > 0 && (
            <>
              <li className="viewer-toggle-section" aria-hidden="true">
                <span>People</span>
              </li>
              {members
                .filter((m) => !selfMember || m.userId !== selfMember.userId)
                .map((m) => (
                  <li key={m.userId}>
                    <button
                      type="button"
                      className={value === m.userId ? 'is-selected' : ''}
                      onClick={() => {
                        onChange(m.userId);
                        setOpen(false);
                      }}
                      role="option"
                      aria-selected={value === m.userId}
                    >
                      <AssigneeAvatar member={m} size={18} />
                      <span>{m.displayName}</span>
                    </button>
                  </li>
                ))}
            </>
          )}
          {userTeams.length > 0 && (
            <>
              <li className="viewer-toggle-section" aria-hidden="true">
                <span>Teams</span>
              </li>
              {userTeams.map((t) => {
                const selVal = `${VIEWER_TEAM_PREFIX}${t.teamId}`;
                return (
                  <li key={t.teamId}>
                    <button
                      type="button"
                      className={value === selVal ? 'is-selected' : ''}
                      onClick={() => {
                        onChange(selVal);
                        setOpen(false);
                      }}
                      role="option"
                      aria-selected={value === selVal}
                    >
                      <span
                        className="viewer-team-dot"
                        style={{ backgroundColor: t.color }}
                        aria-hidden="true"
                      />
                      <span>{t.displayName}</span>
                    </button>
                  </li>
                );
              })}
            </>
          )}
        </ul>
      )}
    </div>
  );
}
